import React, { useState } from 'react';
import { 
  Search, 
  BookOpen, 
  Clock, 
  Calendar, 
  ArrowRight, 
  User, 
  Tag, 
  Sparkles, 
  ChevronLeft,
  Filter,
  CheckCircle2,
  Share2
} from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { BLOG_CATEGORIES } from '../../data/blogsData';

const BlogListPage = ({ onSelectArticle, onBackToHome }) => {
  const { blogs = [] } = useStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Filter published articles
  const publishedArticles = blogs.filter(b => b.status === 'published');

  const filteredArticles = publishedArticles.filter(article => {
    const matchesCategory = selectedCategory === 'All' || selectedCategory === 'all' || article.category === selectedCategory;
    const query = searchQuery.toLowerCase().trim();
    if (!query) return matchesCategory;

    const titleText = (article.titleUrdu || article.title || '').toLowerCase();
    const excerptText = (article.excerptUrdu || article.excerpt || '').toLowerCase();
    const authorText = (article.author || '').toLowerCase();
    const tagsMatch = article.tags && article.tags.some(t => t.toLowerCase().includes(query));

    const matchesSearch = 
      titleText.includes(query) ||
      excerptText.includes(query) ||
      authorText.includes(query) ||
      tagsMatch;

    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50/70 pb-20 pt-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Navigation & Header */}
        <div className="mb-8">
          <button
            type="button"
            onClick={onBackToHome}
            className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-emerald-800 hover:text-emerald-950 transition-colors mb-4 cursor-pointer font-urdu bg-emerald-50 px-3.5 py-1.5 rounded-lg border border-emerald-200"
          >
            <ArrowRight className="w-4 h-4" />
            <span>مرکزی صفحہ پر واپس جائیں</span>
          </button>

          <div className="bg-gradient-to-r from-emerald-900 via-emerald-850 to-pansar-900 rounded-3xl p-6 sm:p-10 text-white shadow-xl relative overflow-hidden">
            {/* Background Decorative Pattern */}
            <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-700/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-10 -right-10 w-80 h-80 bg-amber-500/10 rounded-full blur-2xl pointer-events-none" />

            <div className="relative z-10 max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-800/80 border border-emerald-600/50 text-emerald-200 text-xs font-bold mb-3 font-urdu">
                <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                <span>طبی و غذائی آرٹیکلز کی مکمل لائبریری</span>
              </div>
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black font-urdu mb-3 leading-tight">
                صحت، نبوی غذائیں اور جڑی بوٹیوں کے مستند مضامین
              </h1>
              <p className="text-xs sm:text-sm md:text-base text-emerald-100 font-urdu leading-relaxed">
                حکیم مولانا سید عبدالوہاب شاہ شیرازی کے قلم سے خالص دیسی ادویات، غذائی علاج، اور جڑی بوٹیوں کے خواص و سائنسی فوائد پر جامع اور مستند تحریریں۔
              </p>
            </div>
          </div>
        </div>

        {/* Filter & Search Bar */}
        <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-gray-200/80 mb-8 space-y-4">
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
            
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="w-5 h-5 absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="مضمون کا عنوان، جڑی بوٹی یا بیماری کا نام تلاش کریں..."
                className="w-full pl-4 pr-12 py-3 rounded-xl border border-gray-200 text-xs sm:text-sm font-urdu focus:outline-none focus:ring-2 focus:ring-emerald-600 bg-gray-50 focus:bg-white transition"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600 font-bold px-2 py-1 bg-gray-200 rounded-md cursor-pointer"
                >
                  صاف کریں
                </button>
              )}
            </div>

            {/* Article Count Indicator */}
            <div className="text-xs text-gray-500 font-urdu whitespace-nowrap self-end md:self-center">
              دستیاب مضامین: <span className="font-bold text-emerald-800 font-sans">{filteredArticles.length}</span>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
            <button
              onClick={() => setSelectedCategory('All')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap font-urdu cursor-pointer flex items-center gap-1.5 ${
                selectedCategory === 'All'
                  ? 'bg-emerald-900 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span>تمام شعبے ({publishedArticles.length})</span>
            </button>

            {BLOG_CATEGORIES.map((cat) => {
              const count = publishedArticles.filter(a => a.category === cat.id).length;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap font-urdu cursor-pointer flex items-center gap-1.5 ${
                    selectedCategory === cat.id
                      ? 'bg-emerald-900 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span>{cat.nameUrdu}</span>
                  {count > 0 && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-sans ${
                      selectedCategory === cat.id ? 'bg-emerald-800 text-emerald-100' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Article Cards Grid */}
        {filteredArticles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {filteredArticles.map((article) => (
              <article 
                key={article.id}
                onClick={() => onSelectArticle(article)}
                className="group bg-white rounded-3xl overflow-hidden border border-gray-200/90 hover:border-emerald-600/40 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col cursor-pointer transform hover:-translate-y-1"
              >
                {/* Image Container */}
                <div className="relative aspect-[16/10] overflow-hidden bg-emerald-950">
                  <img
                    src={article.coverImage || article.featuredImage || "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=900&auto=format&fit=crop&q=80"}
                    alt={article.titleUrdu || article.title}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    onError={(e) => { 
                      e.currentTarget.onerror = null; 
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=900&auto=format&fit=crop&q=80'; 
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />
                  
                  {/* Category Badge */}
                  <div className="absolute top-3 right-3">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-900/90 backdrop-blur-xs text-emerald-100 text-[11px] font-bold border border-emerald-700/50 shadow-sm font-urdu">
                      <Tag className="w-3 h-3 text-amber-400" />
                      <span>{article.categoryUrdu || article.category || 'جڑی بوٹیاں'}</span>
                    </span>
                  </div>

                  {/* Read Time */}
                  <div className="absolute bottom-3 right-3 flex items-center gap-1.5 text-white/90 text-xs font-urdu bg-black/50 backdrop-blur-xs px-2.5 py-0.5 rounded-lg">
                    <Clock className="w-3.5 h-3.5 text-emerald-300" />
                    <span>{article.readTime || '5 منٹ کا مطالعہ'}</span>
                  </div>
                </div>

                {/* Content Box */}
                <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                  <div className="space-y-3 text-right">
                    <div className="flex items-center justify-between text-xs text-gray-500 font-urdu border-b border-gray-100 pb-2">
                      <span className="flex items-center gap-1 text-emerald-800 font-bold">
                        <User className="w-3.5 h-3.5" />
                        <span>{article.author || 'حکیم مولانا سید عبدالوہاب شاہ شیرازی'}</span>
                      </span>
                      <span className="flex items-center gap-1 text-gray-400 font-sans text-[11px]">
                        <Calendar className="w-3 h-3" />
                        <span>{article.publishedDate || article.createdAt}</span>
                      </span>
                    </div>

                    <h2 className="font-bold text-base sm:text-lg text-gray-900 font-urdu leading-snug group-hover:text-emerald-800 transition-colors">
                      {article.titleUrdu || article.title}
                    </h2>

                    <p className="text-xs sm:text-sm text-gray-600 font-urdu leading-relaxed line-clamp-3">
                      {article.excerptUrdu || article.excerpt}
                    </p>
                  </div>

                  {/* Bottom Card Action */}
                  <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs font-bold text-emerald-700 font-urdu flex items-center gap-1 group-hover:text-emerald-900 transition-colors">
                      <span>مکمل مضمون پڑھیں</span>
                      <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                    </span>

                    {(article.tags?.[0] || article.focusKeywords) && (
                      <span className="text-[10px] text-gray-500 bg-gray-50 px-2 py-0.5 rounded border border-gray-100 font-urdu">
                        {article.tags?.[0] || article.focusKeywords?.split(',')[0]}
                      </span>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-3xl p-12 text-center border border-gray-200 shadow-sm max-w-lg mx-auto">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-bold text-base text-gray-800 font-urdu">کوئی مضمون نہیں ملا</h3>
            <p className="text-xs text-gray-500 font-urdu mt-1">
              آپ کی تلاش کے مطابق فی الحال کوئی مضمون دستیاب نہیں۔ براہ کرم کوئی دوسرا لفظ تلاش کریں یا تمام شعبے منتخب کریں۔
            </p>
            <button
              onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}
              className="mt-4 px-4 py-2 bg-emerald-800 text-white rounded-xl text-xs font-bold font-urdu cursor-pointer"
            >
              تمام مضامین دکھائیں
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default BlogListPage;
